import { FC, useEffect, useRef, useState } from 'react'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import styles from './multiKeywords.module.scss'
import {
  MultiCampaign,
  MultiKeywordRow,
  MultiKeywordTable,
} from '../interfaces'
import { campaignColor } from '../palette'

interface MultiKeywordsProps {
  campaigns: MultiCampaign[]
  tables: MultiKeywordTable[]
}

const COPY = {
  keyword: 'Keyword',
  mentions: 'Mentions',
  subtitle:
    'See the number of mentions for each keyword during the selected period.',
  title: 'Keywords Comparison',
}

const formatCount = (value: number) => value.toLocaleString('en-US')

/*
 * One campaign's keyword list, faded at its foot only when there is more below.
 *
 * The fade was drawn unconditionally, so a card whose rows all fit — three
 * keywords in a card sized for six — dimmed its own last row for no reason. QA
 * read that as the row being disabled.
 */
const KeywordList: FC<{ rows: MultiKeywordRow[] }> = ({ rows }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const node = ref.current

    if (!node) return undefined

    const measure = () =>
      setOverflows(node.scrollHeight - node.clientHeight > 1)

    measure()

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null

    observer?.observe(node)
    window.addEventListener('resize', measure)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [rows])

  return (
    <>
      <div className={styles.rows} ref={ref}>
        {rows.map((row) => (
          <div className={styles.row} key={row.keyword}>
            <TruncatedTooltip className={styles.keyword} text={row.keyword} />
            <span className={styles.number}>{formatCount(row.mentions)}</span>
          </div>
        ))}
      </div>

      {overflows && <span className={styles.fade} />}
    </>
  )
}

// One card per campaign, each its own small table. Not one table with a column
// per campaign: a keyword that ranks for one campaign usually does not rank for
// the others, so the shared rows would be mostly blank.
export const MultiKeywords: FC<MultiKeywordsProps> = ({
  campaigns,
  tables,
}) => (
  <section className={styles.card}>
    <div className={styles.header}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>{COPY.subtitle}</p>
    </div>

    <div className={styles.columns}>
      {campaigns.map((campaign, index) => {
        const table = tables.find((item) => item.campaignId === campaign.id)

        return (
          <article className={styles.column} key={campaign.id}>
            <header className={styles.columnHeader}>
              <span className={styles.columnTitle}>
                <span
                  className={styles.dot}
                  style={{ background: campaignColor(index) }}
                />
                <TruncatedTooltip
                  className={styles.columnName}
                  maxLines={2}
                  text={campaign.name}
                />
              </span>

              <span className={styles.columnPeriod}>{table?.period}</span>
            </header>

            <div className={styles.tableHead}>
              <span>{COPY.keyword}</span>
              <span className={styles.number}>{COPY.mentions}</span>
            </div>

            <KeywordList rows={table?.rows ?? []} />
          </article>
        )
      })}
    </div>
  </section>
)
